import React, { useState, useEffect, useCallback, useRef } from "react";
import { PAGE_SIZE } from "../../../api";
import {
  fetchServerStats,
  fetchServers as fetchServersApi,
  fetchTopology as fetchTopologyApi,
  fetchApplicationSummary as fetchApplicationSummaryApi,
} from "../services/CMDBService";
import Icon from "../../../components/Icon";
import {
  StatusBadge,
  getApplicationStatus,
  getServerStatus,
} from "../../../components/Badge";
import Skeleton from "../../../components/Skeleton";
import { EmptyState, ErrorState } from "../../../components/State";
import DataTable from "../../../components/DataTable";
import SearchInput from "../../../components/SearchInput";
import FilterSelect from "../../../components/FilterSelect";
import { KpiGrid, KpiCard } from "../../../components/KpiCard";
import ServerDetailModal from "../../../components/ServerDetailModal";
import ApplicationDetailModal from "../../../components/ApplicationDetailModal";
import TopologyView from "../../../components/TopologyView";
import {
  getAssetCategory,
  formatCategoryLabel,
} from "../../../utils/assetTaxonomy";

const healthPercentFor = (asset) => {
  if (getServerStatus(asset) === "inactive") return 0;
  if (asset?.health_percent === null || asset?.health_percent === undefined || asset?.health_percent === "") {
    return null;
  }
  const value = Number(asset?.health_percent);
  return Number.isFinite(value) ? value : null;
};

export default function CMDBView() {
  const [servers, setServers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [topology, setTopology] = useState(null);
  const [applications, setApplications] = useState([]);
  const [serversLoading, setServersLoading] = useState(true);
  const [serversRefreshing, setServersRefreshing] = useState(false);
  const [topologyLoading, setTopologyLoading] = useState(false);
  const [applicationsLoading, setApplicationsLoading] = useState(false);
  const [serversError, setServersError] = useState(null);
  const [serversRefreshError, setServersRefreshError] = useState(null);
  const [statsError, setStatsError] = useState(null);
  const [topologyError, setTopologyError] = useState(null);
  const [applicationsError, setApplicationsError] = useState(null);
  const [selectedServer, setSelectedServer] = useState(null);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [activeTab, setActiveTab] = useState("servers");
  const [hasServers, setHasServers] = useState(true);
  const hasLoadedServersRef = useRef(false);
  const serversRequestIdRef = useRef(0);

  const [searchQuery, setSearchQuery] = useState("");
  const [appSearchQuery, setAppSearchQuery] = useState("");
  const [appStatusFilter, setAppStatusFilter] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [page, setPage] = useState(1);
  const [appPage, setAppPage] = useState(1);
  const [hoveredCard, setHoveredCard] = useState(null);

  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    issues: 0,
  });

  const fetchStats = useCallback(async () => {
    try {
      const data = await fetchServerStats();
      if (data) {
        setStats(data);
        setStatsError(null);
      } else {
        setStatsError("Could not load asset summary counts.");
      }
    } catch (e) {
      setStatsError("Could not load asset summary counts.");
      console.error("Could not load server stats:", e);
    }
  }, []);

  const fetchServers = useCallback(async ({ background = false } = {}) => {
    const requestId = ++serversRequestIdRef.current;
    const isBackgroundRefresh = background && hasLoadedServersRef.current;

    if (isBackgroundRefresh) {
      setServersRefreshing(true);
      setServersRefreshError(null);
    } else {
      setServersLoading(true);
      setServersRefreshing(false);
      setServersError(null);
      setServersRefreshError(null);
    }

    try {
      const offset = (page - 1) * PAGE_SIZE;
      const data = await fetchServersApi({
        limit: PAGE_SIZE,
        offset: offset,
        search: appliedSearch,
        status: statusFilter,
      });

      if (requestId !== serversRequestIdRef.current) return;

      const items = data?.items || [];
      const total = data?.total || 0;
      setServers(items);
      setTotalCount(total);
      setServersError(null);
      setServersRefreshError(null);
      hasLoadedServersRef.current = true;
      if (!appliedSearch && !statusFilter) {
        setHasServers(total > 0 || items.length > 0);
      }
    } catch {
      if (requestId !== serversRequestIdRef.current) return;

      if (isBackgroundRefresh) {
        setServersRefreshError(
          "Could not refresh servers. Showing the last loaded data.",
        );
      } else {
        setServersError("Could not load servers.");
      }
    } finally {
      if (requestId === serversRequestIdRef.current) {
        if (isBackgroundRefresh) {
          setServersRefreshing(false);
        } else {
          setServersLoading(false);
        }
      }
    }
  }, [page, appliedSearch, statusFilter]);

  const fetchTopology = useCallback(async () => {
    setTopologyLoading(true);
    setTopologyError(null);
    try {
      setTopology(await fetchTopologyApi());
    } catch {
      setTopologyError("Could not load topology.");
    } finally {
      setTopologyLoading(false);
    }
  }, []);

  const fetchApplications = useCallback(async () => {
    setApplicationsLoading(true);
    setApplicationsError(null);
    try {
      const data = await fetchApplicationSummaryApi();
      setApplications(data || []);
    } catch {
      setApplicationsError("Could not load application summaries.");
    } finally {
      setApplicationsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab !== "servers") return;

    let cancelled = false;
    let serverRefreshTimeout;

    const refreshServers = async (background = false) => {
      await fetchServers({ background });
      if (!cancelled) {
        serverRefreshTimeout = setTimeout(
          () => refreshServers(true),
          10000,
        );
      }
    };

    refreshServers();
    fetchStats();
    const statsInterval = setInterval(fetchStats, 10000);

    return () => {
      cancelled = true;
      clearTimeout(serverRefreshTimeout);
      clearInterval(statsInterval);
    };
  }, [activeTab, fetchServers, fetchStats]);

  useEffect(() => {
    if (activeTab === "topology") {
      if (!topology) fetchTopology();
      if (applications.length === 0) fetchApplications();
    }
    if (activeTab === "applications") fetchApplications();
  }, [
    activeTab,
    topology,
    applications.length,
    fetchTopology,
    fetchApplications,
  ]);

  // Debounce search query changes
  useEffect(() => {
    const timer = setTimeout(() => {
      if (appliedSearch !== searchQuery) {
        setAppliedSearch(searchQuery);
        setPage(1);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [searchQuery, appliedSearch]);

  const handleStatusFilterChange = (val) => {
    setStatusFilter(val);
    setPage(1);
  };

  // Detect if "hosted_on" has any data
  const showHostedOn = servers.some((s) => s.hosted_on);

  const activeCount = stats.active;
  const issueCount = stats.issues;

  const tabs = [
    { id: "servers", label: "Assets", icon: "server" },
    { id: "applications", label: "Applications", icon: "layers" },
    { id: "topology", label: "Topology", icon: "network" },
  ];

  const statCards = [
    {
      id: "",
      label: "Total assets",
      value: stats.total,
      color: "#1D4ED8",
      bg: "#EFF6FF",
      icon: "server",
    },
    {
      id: "active",
      label: "Active",
      value: activeCount,
      color: "#15803D",
      bg: "#F0FDF4",
      icon: "check",
    },
    {
      id: "inactive",
      label: "Issues",
      value: issueCount,
      color: "#DC2626",
      bg: "#FEF2F2",
      icon: "alert",
    },
  ];

  const serverColumns = [
    {
      header: "Name",
      tip: "CI name and identifier",
      render: (_, server) => {
        const cat = getAssetCategory(server);
        const isRouter = cat === "router";
        const isDb = cat === "database" || cat === "db";
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div
              style={{
                width: 26,
                height: 26,
                borderRadius: 6,
                background: isRouter ? "#F3E8FF" : isDb ? "#FEF3C7" : "#EFF6FF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                border: isRouter
                  ? "1px solid #E9D5FF"
                  : isDb
                    ? "1px solid #FDE68A"
                    : "1px solid #BFDBFE",
              }}
            >
              <Icon
                name={isRouter ? "network" : isDb ? "database" : "server"}
                size={13}
                color={isRouter ? "#6B21A8" : isDb ? "#92400E" : "#1D4ED8"}
              />
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#0F172A" }}>
                {server.name}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: "#64748B",
                  fontFamily: "monospace",
                  whiteSpace: "nowrap",
                }}
              >
                {server.id}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      header: "Hostname",
      tip: "Network hostname / FQDN",
      cellStyle: { fontFamily: "monospace", color: "#475569" },
      render: (_, server) => server.hostname,
    },
    {
      header: "Status",
      tip: "Current operational status",
      render: (_, server) => <StatusBadge entity={server} type="server" />,
    },
    {
      header: "Category",
      tip: "Asset category, such as Server or Router",
      render: (_, server) => {
        const cat = getAssetCategory(server);
        const isRouter = cat === "router";
        const isDb = cat === "database" || cat === "db";
        return (
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.03em",
              padding: "3px 8px",
              borderRadius: 6,
              background: isRouter ? "#F3E8FF" : isDb ? "#FEF3C7" : "#EFF6FF",
              color: isRouter ? "#6B21A8" : isDb ? "#92400E" : "#1E40AF",
              border: isRouter
                ? "1px solid #E9D5FF"
                : isDb
                  ? "1px solid #FDE68A"
                  : "1px solid #BFDBFE",
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              textTransform: "uppercase",
            }}
          >
            <Icon
              name={isRouter ? "network" : isDb ? "database" : "server"}
              size={10}
              color={isRouter ? "#6B21A8" : isDb ? "#92400E" : "#1E40AF"}
            />
            {formatCategoryLabel(cat)}
          </span>
        );
      },
    },
    {
      header: "Health",
      tip: "Health percentage returned by CMDB",
      render: (_, server) => {
        const healthPercent = healthPercentFor(server);
        if (healthPercent === null) {
          return <span style={{ color: "#94A3B8" }}>—</span>;
        }
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 7,
              minWidth: 92,
            }}
          >
            <span
              style={{
                width: 48,
                height: 5,
                borderRadius: 3,
                background: "#E2E8F0",
                overflow: "hidden",
              }}
            >
              <span
                style={{
                  display: "block",
                  width: `${healthPercent}%`,
                  height: "100%",
                  background:
                    healthPercent >= 80
                      ? "#16A34A"
                      : healthPercent >= 60
                        ? "#D97706"
                        : "#DC2626",
                }}
              />
            </span>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#334155" }}>
              {healthPercent === 0 ? "0%" : `${healthPercent.toFixed(1)}%`}
            </span>
          </div>
        );
      },
    },
    {
      header: "OS / Model",
      tip: "Operating system or Device model",
      render: (_, server) => {
        const osVal =
          server.os ||
          server.specs?.os ||
          server.specs?.model ||
          (server.specs?.vendor ? `${server.specs.vendor} Device` : null);
        return osVal ? (
          <span
            style={{
              fontSize: 11,
              background: "#F1F5F9",
              color: "#334155",
              padding: "2px 8px",
              borderRadius: 8,
              border: "1px solid #E2E8F0",
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <Icon name="cpu" size={10} color="#64748B" />
            {osVal}
          </span>
        ) : (
          <span style={{ color: "#CBD5E1", fontSize: 12 }}>—</span>
        );
      },
    },
    ...(showHostedOn
      ? [
          {
            header: "Hosted on",
            tip: "Cloud or hosting platform",
            render: (_, server) => {
              const rawHost =
                server.hosted_on ||
                server.specs?.cloud_provider ||
                server.specs?.hosted_on;
              const hostVal =
                !rawHost || rawHost.toLowerCase().includes("premise")
                  ? "GCP"
                  : rawHost.toUpperCase();
              return (
                <span
                  style={{
                    fontSize: 11,
                    background: "#EFF6FF",
                    color: "#1D4ED8",
                    padding: "2px 8px",
                    borderRadius: 8,
                    border: "1px solid #BFDBFE",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  <Icon name="layers" size={10} color="#1D4ED8" />
                  {hostVal}
                </span>
              );
            },
          },
        ]
      : []),
    {
      header: "Last seen",
      tip: "Most recent telemetry or scan update",
      cellStyle: { color: "#64748B", whiteSpace: "nowrap" },
      render: (_, server) =>
        server.last_seen_at ? (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              cursor: "default",
            }}
          >
            <Icon name="clock" size={11} color="#64748B" />
            {new Date(server.last_seen_at).toLocaleString("en-IN", {
              day: "2-digit",
              month: "short",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
            })}
          </span>
        ) : (
          "—"
        ),
    },
  ];

  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        background: "#F8FAFC",
      }}
    >
      <div
        style={{
          padding: "18px 24px 14px",
          borderBottom: "1px solid #E5E7EB",
          background: "#fff",
        }}
      >
        <div style={{ marginBottom: 14 }}>
          <h1
            style={{
              margin: 0,
              fontSize: 18,
              fontWeight: 700,
              color: "#111827",
            }}
          >
            CMDB
          </h1>
          <p style={{ margin: "2px 0 0", fontSize: 12, color: "#6B7280" }}>
            Configuration Management Database — servers, services & topology
          </p>
        </div>
        {/* Tabs */}
        <div
          style={{
            display: "flex",
            borderBottom: "1px solid #F3F4F6",
            marginBottom: activeTab === "servers" ? 12 : 0,
          }}
        >
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => {
                setActiveTab(t.id);
                setSelectedServer(null);
              }}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 14px",
                border: "none",
                background: "none",
                cursor: "pointer",
                fontSize: 12,
                fontWeight: activeTab === t.id ? 700 : 400,
                color: activeTab === t.id ? "#1D4ED8" : "#6B7280",
                borderBottom:
                  activeTab === t.id
                    ? "2px solid #1D4ED8"
                    : "2px solid transparent",
              }}
            >
              <Icon
                name={t.icon}
                size={14}
                color={activeTab === t.id ? "#1D4ED8" : "#9CA3AF"}
              />
              {t.label}
            </button>
          ))}
        </div>

        {activeTab === "servers" && hasServers && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <SearchInput
              placeholder="Search servers by name or hostname…"
              value={searchQuery}
              onChange={setSearchQuery}
              style={{ flex: 1, minWidth: 200 }}
            />

            <FilterSelect
              value={statusFilter}
              onChange={handleStatusFilterChange}
              placeholder="All statuses"
              options={[
                { value: "active", label: "Active" },
                { value: "inactive", label: "Issues / Inactive" },
                { value: "decommissioned", label: "Decommissioned" },
              ]}
            />
          </div>
        )}
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: "auto" }}>
        {activeTab === "servers" && (
          <div style={{ padding: "16px 24px" }}>
            <KpiGrid columns={3} style={{ marginBottom: 16 }}>
              {statCards.map((c) => (
                <KpiCard
                  key={c.label}
                  icon={c.icon}
                  label={c.label}
                  value={c.value}
                  color={c.color}
                  bg={c.bg}
                  active={statusFilter === c.id}
                  onClick={() => handleStatusFilterChange(c.id)}
                />
              ))}
            </KpiGrid>
            {statsError && (
              <div
                role="alert"
                style={{
                  margin: "-8px 0 12px",
                  color: "#B91C1C",
                  fontSize: 12,
                }}
              >
                {statsError}
              </div>
            )}
            <div
              role="status"
              aria-live="polite"
              style={{
                minHeight: 18,
                marginBottom: 6,
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 11,
                color: serversRefreshError ? "#B91C1C" : "#64748B",
              }}
            >
              {serversRefreshing && (
                <>
                  <Icon name="refresh" size={12} />
                  <span>Refreshing assets...</span>
                </>
              )}
              {serversRefreshError && (
                <>
                  <Icon name="alert" size={12} />
                  <span>{serversRefreshError}</span>
                  <button
                    type="button"
                    onClick={() => fetchServers({ background: true })}
                    style={{
                      border: 0,
                      padding: 0,
                      background: "transparent",
                      color: "#1D4ED8",
                      cursor: "pointer",
                      fontSize: 11,
                      fontWeight: 600,
                    }}
                  >
                    Retry
                  </button>
                </>
              )}
            </div>
            <DataTable
              columns={serverColumns}
              data={servers}
              loading={serversLoading}
              error={serversError}
              onRetry={fetchServers}
              emptyTitle={
                hasServers ? "No results found" : "No servers registered"
              }
              emptySubtitle={
                hasServers
                  ? "No matching items were found for your search query. Try clearing the search or adjusting filters."
                  : "No servers are registered in the CMDB."
              }
              emptyIcon={hasServers ? "search" : "server"}
              onRowClick={(server) => setSelectedServer(server)}
              page={page}
              total={totalCount}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
              itemLabel="assets"
            />
          </div>
        )}
        {activeTab === "applications" && (
          <div style={{ padding: "20px 24px" }}>
            {/* Filter Bar */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 20,
                flexWrap: "wrap",
                gap: 12,
              }}
            >
              <div style={{ display: "flex", gap: 10, flex: 1, minWidth: 260 }}>
                <SearchInput
                  placeholder="Search applications by name, code, or owner..."
                  value={appSearchQuery}
                  onChange={(val) => {
                    setAppSearchQuery(val);
                    setAppPage(1);
                  }}
                  style={{ flex: 1 }}
                />

                <FilterSelect
                  value={appStatusFilter}
                  onChange={(val) => {
                    setAppStatusFilter(val);
                    setAppPage(1);
                  }}
                  placeholder="All Statuses"
                  options={[
                    { value: "healthy", label: "Healthy (≥90%)" },
                    { value: "degraded", label: "Degraded (50-89%)" },
                    { value: "critical", label: "Critical (<50%)" },
                    { value: "inactive", label: "Inactive" },
                  ]}
                />
              </div>

              <div style={{ fontSize: 12, color: "#6B7280" }}>
                Showing{" "}
                <strong style={{ color: "#111827" }}>
                  {
                    applications.filter((a) => {
                      const isRoot = !a.parent_id || a.is_parent;
                      const matchesSearch =
                        !appSearchQuery ||
                        a.name
                          .toLowerCase()
                          .includes(appSearchQuery.toLowerCase()) ||
                        (a.code &&
                          a.code
                            .toLowerCase()
                            .includes(appSearchQuery.toLowerCase())) ||
                        (a.owner_team &&
                          a.owner_team
                            .toLowerCase()
                            .includes(appSearchQuery.toLowerCase()));
                      const appStatus = getApplicationStatus(a);
                      const matchesStatus =
                        !appStatusFilter || appStatus === appStatusFilter || a.status === appStatusFilter;
                      return isRoot && matchesSearch && matchesStatus;
                    }).length
                  }
                </strong>{" "}
                platforms
              </div>
            </div>

            {/* Applications Grid */}
            {applicationsLoading ? (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
                  gap: 16,
                }}
              >
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} height={150} style={{ borderRadius: 8 }} />
                ))}
              </div>
            ) : applicationsError ? (
              <ErrorState
                message={applicationsError}
                onRetry={fetchApplications}
              />
            ) : (
              (() => {
                const APP_PAGE_SIZE = 12;
                const filtered = applications.filter((a) => {
                  const isRoot = !a.parent_id || a.is_parent;
                  const matchesSearch =
                    !appSearchQuery ||
                    a.name
                      .toLowerCase()
                      .includes(appSearchQuery.toLowerCase()) ||
                    (a.code &&
                      a.code
                        .toLowerCase()
                        .includes(appSearchQuery.toLowerCase())) ||
                    (a.owner_team &&
                      a.owner_team
                        .toLowerCase()
                        .includes(appSearchQuery.toLowerCase()));
                  const appStatus = getApplicationStatus(a);
                  const matchesStatus =
                    !appStatusFilter || appStatus === appStatusFilter || a.status === appStatusFilter;
                  return isRoot && matchesSearch && matchesStatus;
                });

                if (filtered.length === 0) {
                  return (
                    <EmptyState
                      title="No applications found"
                      message="No platform applications match the current search or status filter."
                    />
                  );
                }

                const totalAppPages = Math.max(
                  1,
                  Math.ceil(filtered.length / APP_PAGE_SIZE),
                );
                const safeAppPage = Math.min(appPage, totalAppPages);
                const pagedApps = filtered.slice(
                  (safeAppPage - 1) * APP_PAGE_SIZE,
                  safeAppPage * APP_PAGE_SIZE,
                );

                return (
                  <>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fill, minmax(340px, 1fr))",
                        gap: 16,
                      }}
                    >
                      {pagedApps.map((app) => {
                        const isHovered = hoveredCard === app.id;

                        return (
                          <div
                            key={app.id}
                            onClick={() => setSelectedApplication(app)}
                            onMouseEnter={() => setHoveredCard(app.id)}
                            onMouseLeave={() => setHoveredCard(null)}
                            style={{
                              background: "#FFFFFF",
                              borderRadius: 8,
                              border: isHovered
                                ? "1px solid #93C5FD"
                                : "1px solid #E2E8F0",
                              padding: "16px 18px",
                              boxShadow: isHovered
                                ? "0 4px 12px rgba(0,0,0,0.06)"
                                : "0 1px 3px rgba(0,0,0,0.03)",
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "space-between",
                              gap: 12,
                              cursor: "pointer",
                              transition: "all 0.15s ease",
                              transform: isHovered
                                ? "translateY(-1px)"
                                : "none",
                            }}
                          >
                            <div>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "flex-start",
                                  gap: 8,
                                }}
                              >
                                <div>
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 6,
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontWeight: 700,
                                        fontSize: 14,
                                        color: isHovered
                                          ? "#1D4ED8"
                                          : "#0F172A",
                                        transition: "color 0.15s ease",
                                      }}
                                    >
                                      {app.name}
                                    </span>
                                    {app.code && (
                                      <span
                                        style={{
                                          fontSize: 10,
                                          fontWeight: 600,
                                          padding: "1px 5px",
                                          background: "#F1F5F9",
                                          borderRadius: 4,
                                          color: "#475569",
                                        }}
                                      >
                                        {app.code}
                                      </span>
                                    )}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 11,
                                      color: "#64748B",
                                      marginTop: 3,
                                    }}
                                  >
                                    Platform Root
                                    {app.owner_team && ` • ${app.owner_team}`}
                                  </div>
                                </div>

                                <StatusBadge
                                  entity={app}
                                  type="application"
                                />
                              </div>

                              {app.description && (
                                <p
                                  style={{
                                    margin: "8px 0 0",
                                    fontSize: 12,
                                    color: "#475569",
                                    lineHeight: 1.4,
                                  }}
                                >
                                  {app.description}
                                </p>
                              )}
                            </div>

                            <div
                              style={{
                                paddingTop: 10,
                                borderTop: "1px solid #F1F5F9",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 10,
                                  fontSize: 11,
                                  color: "#64748B",
                                }}
                              >
                                <span>
                                  <strong style={{ color: "#0F172A" }}>
                                    {app.services_count}
                                  </strong>{" "}
                                  Services
                                </span>
                                <span>•</span>
                                <span>
                                  <strong
                                    style={{
                                      color:
                                        app.open_tickets_count > 0
                                          ? "#DC2626"
                                          : "#0F172A",
                                    }}
                                  >
                                    {app.open_tickets_count}
                                  </strong>{" "}
                                  Tickets
                                </span>
                              </div>

                              <span
                                style={{
                                  color: isHovered ? "#1D4ED8" : "#94A3B8",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  transition: "color 0.15s ease, transform 0.15s ease",
                                  transform: isHovered
                                    ? "translateX(2px)"
                                    : "none",
                                }}
                              >
                                <Icon name="chevronRight" size={13} />
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                );
              })()
            )}
          </div>
        )}

        {activeTab === "topology" && (
          <div style={{ padding: "20px 24px" }}>
            {topologyError ? (
              <ErrorState message={topologyError} onRetry={fetchTopology} />
            ) : topologyLoading ? (
              <div
                style={{ display: "flex", flexDirection: "column", gap: 10 }}
              >
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} height={80} style={{ borderRadius: 8 }} />
                ))}
              </div>
            ) : (
              <TopologyView
                topology={topology}
                servers={servers}
                applications={applications}
                initialDomain="infrastructure"
                onSelectServer={setSelectedServer}
                onSelectApplication={setSelectedApplication}
              />
            )}
          </div>
        )}
      </div>

      {selectedServer && (
        <ServerDetailModal
          server={selectedServer}
          onClose={() => setSelectedServer(null)}
        />
      )}

      {selectedApplication && (
        <ApplicationDetailModal
          app={selectedApplication}
          onClose={() => setSelectedApplication(null)}
          onSelectServer={(srv) => {
            setSelectedApplication(null);
            setSelectedServer(srv);
          }}
        />
      )}
    </div>
  );
}
